import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Printer, Download } from "lucide-react";

const money = (value) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value ?? 0));

const logo =
  "https://customer-assets-lqy194kg.emergentagent.net/job_nityavastra-shop/artifacts/dirh3az1_Updated%20Logo%20Nityavastra.png";

const dateLabel = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "—"
    : date.toLocaleDateString("en-IN", {
        day: "numeric",
        month: "long",
        year: "numeric",
        timeZone: "Asia/Kolkata",
      });
};

function Detail({ label, children }) {
  return (
    <div className="nv-detail">
      <span>{label}</span>
      <span>{children || "—"}</span>
    </div>
  );
}

export default function Invoice() {
  const { id } = useParams();
  const { user, loading: authLoading } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (authLoading) return;
    setData(null);
    setError("");

    if (!user) {
      setError("Please sign in to view your invoice.");
      return;
    }

    let active = true;
    api.get(`/orders/${id}/invoice`)
      .then(({ data: result }) => {
        if (active) setData(result);
      })
      .catch((err) => {
        if (!active) return;
        const detail = err.response?.data?.detail;
        setError(
          typeof detail === "string" ? detail : "Invoice unavailable."
        );
      });

    return () => { active = false; };
  }, [id, user, authLoading]);

  if (error || !data) {
    return (
      <div className="mx-auto max-w-2xl px-6 pt-40 pb-16 text-center">
        {error || "Loading invoice..."}
      </div>
    );
  }

  const { order, seller } = data;
  const gst = order.gst || {};
  const address = order.shipping_address || {};
  const items = order.items || [];
  const discount = Number(order.coupon_discount ?? 0);
  const shipping = Number(order.shipping_inr ?? 0);
  const quote = order.shipping_quote || {};

  // Display only recorded courier charges, never an invented shipping price.
  const courierCost = Number(
    order.courier_cost_inr ?? quote.courier_cost_inr ?? 0
  );
  const shippingPaidBy =
    order.shipping_paid_by ?? quote.shipping_paid_by;
  const hasShippingWaiver =
    shipping === 0 &&
    courierCost > 0 &&
    shippingPaidBy === "nityavastra";

  const subtotal = Number(
    order.subtotal_inr ??
    items.reduce(
      (sum, item) =>
        sum + Number(item.price ?? 0) * Number(item.quantity ?? 0),
      0
    )
  );

  const paymentLabels = {
    cod: "Cash on Delivery",
    upi: "UPI",
    card: "Card",
    netbanking: "Net Banking",
    net_banking: "Net Banking",
    wallet: "Wallet",
    emi: "EMI",
    bank_transfer: "Bank Transfer",
    razorpay: "Online payment · Razorpay",
  };

  const rawMethod = String(
    order.razorpay_payment_method ||
    order.payment_details?.method ||
    order.payment_method ||
    ""
  ).toLowerCase();

  const paymentMethod =
    paymentLabels[rawMethod] || "Not recorded";

  const companyName =
    seller.trade_name ||
    seller.legal_name ||
    seller.name ||
    "SWAPNA ENTERPRISE";

  const taxableTotal =
    gst.taxable_value ??
    Math.max(0, subtotal - discount -
      Number(gst.cgst ?? 0) -
      Number(gst.sgst ?? 0) -
      Number(gst.igst ?? 0));

  return (
    <div className="nv-invoice-page">
      <div className="nv-invoice-wrap">
        <div className="nv-invoice-actions">
          <Button
            variant="outline"
            onClick={() => window.print()}
            data-testid="print-invoice-btn"
            className="rounded-full"
          >
            <Printer className="mr-2 h-4 w-4" />
            Print
          </Button>
          <Button
            onClick={() => window.print()}
            data-testid="download-invoice-btn"
            className="rounded-full bg-[#7C1F30] hover:bg-[#8D2A3D]"
          >
            <Download className="mr-2 h-4 w-4" />
            Download PDF
          </Button>
        </div>

        <article className="nv-invoice" data-testid="invoice-body">
          <header className="nv-brand">
            <img src={logo} alt="Nityavastra logo" />
            <h1>NITYAVASTRA</h1>
            <p>Sacred Weaves · Everyday Grace</p>
          </header>

          <section className="nv-header-details">
            <div className="nv-company">
              <h2>{companyName}</h2>
              <p>{seller.address}</p>
              <p>{seller.email}</p>
              <p>{seller.phone}</p>
            </div>
            <div className="nv-meta">
              <h2>TAX INVOICE</h2>
              <Detail label="Invoice No.">{order.invoice_number}</Detail>
              <Detail label="Invoice Date">
                {dateLabel(order.invoice_issued_at || order.created_at)}
              </Detail>
              <Detail label="GSTIN">{seller.gstin}</Detail>
              <Detail label="Payment Method">{paymentMethod}</Detail>
              {String(order.payment_method || "").toLowerCase() === "cod" ? (
                <Detail label="Delivery">
                  Successfully delivered by the courier.
                </Detail>
              ) : (
                <Detail label="Payment Status">
                  {order.payment_status === "paid"
                    ? "Paid"
                    : order.payment_status || "—"}
                </Detail>
              )}
              {order.razorpay_payment_id && (
                <Detail label="Payment Reference">
                  {order.razorpay_payment_id}
                </Detail>
              )}
            </div>
          </section>

          <section className="nv-buyer">
            <div>
              <h3>Billed To</h3>
              <strong>{address.name || "—"}</strong>
              <p>{address.address}</p>
              <p>
                {[address.city, address.state].filter(Boolean).join(", ")}
                {address.pincode ? ` – ${address.pincode}` : ""}
              </p>
              <p>{address.phone}</p>
              {order.buyer_gstin && <p>GSTIN: {order.buyer_gstin}</p>}
            </div>
            <div className="nv-supply">
              <h3>Place of Supply</h3>
              <strong>{address.state || "—"}</strong>
              <p>
                {gst.same_state
                  ? "Intra-state · CGST + SGST"
                  : "Inter-state · IGST"}
              </p>
            </div>
          </section>

          <div className="nv-table-wrap">
            <table className="nv-items">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>HSN</th>
                  <th>Qty</th>
                  <th>Rate</th>
                  <th>Taxable</th>
                  {gst.same_state ? (
                    <><th>CGST</th><th>SGST</th></>
                  ) : <th>IGST</th>}
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, index) => {
                  const tax = (gst.line_gst || [])[index] || {};
                  const gross = Number(
                    item.subtotal ??
                    Number(item.price ?? 0) * Number(item.quantity ?? 0)
                  );
                  const taxable = Number(
                    tax.taxable_value ?? gross
                  );
                  const totalTax = Number(
                    tax.total_tax ??
                    Number(tax.cgst ?? 0) +
                    Number(tax.sgst ?? 0) +
                    Number(tax.igst ?? 0)
                  );
                  return (
                    <tr key={`${item.product_id || index}-${index}`}>
                      <td>{item.product_name || item.name}</td>
                      <td>{item.hsn || tax.hsn || "—"}</td>
                      <td>{item.quantity}</td>
                      <td>{money(item.price)}</td>
                      <td>{money(taxable)}</td>
                      {gst.same_state ? (
                        <>
                          <td>{money(tax.cgst)}</td>
                          <td>{money(tax.sgst)}</td>
                        </>
                      ) : <td>{money(tax.igst)}</td>}
                      <td>{money(taxable + totalTax)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <section className="nv-summary">
            <div className="nv-tax">
              <h3>GST Breakdown</h3>
              <Detail label="Taxable Value">{money(taxableTotal)}</Detail>
              {gst.same_state ? (
                <>
                  <Detail label="CGST">{money(gst.cgst)}</Detail>
                  <Detail label="SGST">{money(gst.sgst)}</Detail>
                </>
              ) : <Detail label="IGST">{money(gst.igst)}</Detail>}
              <p>Tax shown above is included in the total.</p>
              {discount > 0 && (
                <p>Taxable value and GST reflect the coupon discount.</p>
              )}
            </div>

            <div className="nv-totals">
              <Detail label="Subtotal · GST included">
                {money(subtotal)}
              </Detail>

              {discount > 0 && (
                <div className="nv-saving">
                  <Detail label={`Coupon${order.coupon_code ? ` · ${order.coupon_code}` : ""}`}>
                    −{money(discount)}
                  </Detail>
                </div>
              )}

              {hasShippingWaiver ? (
                <>
                  <Detail label="Courier Shipping Charge">
                    {money(courierCost)}
                  </Detail>
                  <div className="nv-saving">
                    <Detail label="Free Shipping · Paid by Nityavastra">
                      −{money(courierCost)}
                    </Detail>
                  </div>
                </>
              ) : (
                <Detail label="Shipping">
                  {shipping === 0 ? "Free" : money(shipping)}
                </Detail>
              )}

              {Number(order.round_off ?? 0) !== 0 && (
                <Detail label="Round Off">{money(order.round_off)}</Detail>
              )}

              <div className="nv-grand">
                <span>Grand Total</span>
                <strong>{money(order.total_inr)}</strong>
              </div>

              {discount > 0 && (
                <p className="nv-saved-note">
                  Coupon savings: {money(discount)}
                </p>
              )}
            </div>
          </section>

          <footer className="nv-invoice-footer">
            <p>Thank you for choosing Nityavastra.</p>
            <p>We appreciate your trust and look forward to serving you again.</p>
            <p>Electronically generated invoice. No signature required.</p>
          </footer>
        </article>
      </div>

      <style>{`
        .nv-invoice-page {
          background: #faf3e7;
          min-height: 100vh;
          padding: 150px 16px 48px;
        }
        .nv-invoice-wrap { max-width: 980px; margin: auto; }
        .nv-invoice-actions {
          display: flex; justify-content: flex-end; gap: 10px;
          margin-bottom: 18px;
        }
        .nv-invoice {
          position: relative; isolation: isolate;
          background: white; color: #30271f;
          border: 1px solid #e7e5e4;
          border-radius: 8px; padding: 40px;
          font-size: 13px; line-height: 1.6;
        }
        .nv-invoice::before {
          content: "NITYAVASTRA";
          position: absolute; top: 50%; left: 50%;
          transform: translate(-50%, -50%) rotate(-35deg);
          width: 85%; text-align: center; white-space: nowrap;
          font: 400 clamp(20px, 5.5vw, 46px) Georgia, serif;
          letter-spacing: 2px;
          color: rgba(124, 31, 48, .055);
          pointer-events: none; z-index: -1;
          print-color-adjust: exact;
          -webkit-print-color-adjust: exact;
        }
        .nv-brand { text-align: center; margin-bottom: 30px; }
        .nv-brand img {
          display: block; width: 76px; height: 76px;
          object-fit: contain; margin: 0 auto 12px;
        }
        .nv-brand h1 {
          font: 36px Georgia, serif; letter-spacing: 4px;
          color: #7c1f30; margin: 0;
        }
        .nv-brand p {
          color: #a67b25; text-transform: uppercase;
          letter-spacing: 2px; font-size: 11px; margin-top: 8px;
        }
        .nv-header-details {
          display: grid; grid-template-columns: 1fr 1fr;
          gap: 32px; padding-bottom: 24px;
          border-bottom: 1px solid #d9c49c;
        }
        .nv-company h2 {
          font-size: 14px; letter-spacing: 1px;
          font-weight: 600; margin-bottom: 8px;
        }
        .nv-company p { color: #70675e; margin: 3px 0; }
        .nv-meta h2 {
          color: #7c1f30; letter-spacing: 3px;
          text-align: right; margin-bottom: 12px;
        }
        .nv-detail {
          display: flex; justify-content: space-between;
          align-items: baseline; gap: 14px; margin: 7px 0;
        }
        .nv-detail > span:first-child { color: #70675e; }
        .nv-detail > span:last-child {
          text-align: right; overflow-wrap: anywhere;
          font-variant-numeric: tabular-nums;
        }
        .nv-buyer {
          display: grid; grid-template-columns: 1fr 1fr;
          gap: 24px; margin: 26px 0;
        }
        .nv-invoice h3 {
          text-transform: uppercase; letter-spacing: 2px;
          font-size: 10px; color: #8d7962; margin-bottom: 8px;
        }
        .nv-buyer p { margin: 3px 0; color: #70675e; }
        .nv-supply { text-align: right; }
        .nv-table-wrap { overflow-x: auto; }
        .nv-items { width: 100%; border-collapse: collapse; font-size: 12px; }
        .nv-items th, .nv-items td {
          padding: 11px 7px; text-align: right;
          border-bottom: 1px solid #e8dfd2;
          font-variant-numeric: tabular-nums;
        }
        .nv-items th { background: #f8f1e5; font-weight: 500; }
        .nv-items th:first-child, .nv-items td:first-child { text-align: left; }
        .nv-items td:not(:first-child) { white-space: nowrap; }
        .nv-summary {
          display: grid; grid-template-columns: 1fr 1.2fr;
          gap: 30px; margin-top: 28px;
        }
        .nv-tax { padding: 16px; background: #faf7f0; align-self: start; }
        .nv-tax p, .nv-saved-note { font-size: 11px; color: #70675e; margin-top: 10px; }
        .nv-saving, .nv-saving .nv-detail > span:first-child { color: #176963; }
        .nv-grand {
          display: flex; justify-content: space-between;
          gap: 16px; border-top: 1px solid #d9c49c;
          padding-top: 13px; margin-top: 14px;
          font-size: 19px; color: #7c1f30;
        }
        .nv-saved-note { text-align: right; }
        .nv-invoice-footer {
          text-align: center; border-top: 1px solid #e8dfd2;
          margin-top: 32px; padding-top: 22px;
        }
        .nv-invoice-footer p:first-child {
          font: 20px Georgia, serif; color: #7c1f30;
        }
        .nv-invoice-footer p:nth-child(2) {
          margin-top: 6px; font-size: 12px; color: #70675e;
        }
        .nv-invoice-footer p:last-child {
          margin-top: 16px; font-size: 10px;
          font-style: italic; color: #70675e;
        }
        @media screen and (max-width: 640px) {
          .nv-invoice { padding: 22px 16px; }
          .nv-brand h1 { font-size: 28px; letter-spacing: 2px; }
          .nv-header-details, .nv-summary {
            grid-template-columns: 1fr; gap: 20px;
          }
          .nv-meta h2 { text-align: left; }
          .nv-buyer { grid-template-columns: 1fr; }
          .nv-supply { text-align: left; }
        }
        @media print {
          @page { size: A4; margin: 12mm; }
          html, body {
            margin: 0 !important; padding: 0 !important;
            background: white !important;
          }
          body *:not(:has([data-testid="invoice-body"])):not([data-testid="invoice-body"]):not([data-testid="invoice-body"] *) {
            display: none !important;
          }
          body *:has([data-testid="invoice-body"]) {
            margin: 0 !important; padding: 0 !important;
            min-height: 0 !important; max-width: none !important;
            width: auto !important; background: white !important;
          }
          .nv-invoice {
            width: 100% !important; padding: 0 !important;
            margin: 0 !important; border: 0 !important;
            border-radius: 0; font-size: 11px;
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
          .nv-brand { margin-bottom: 20px; }
          .nv-brand img { width: 60px; height: 60px; }
          .nv-brand h1 { font-size: 30px; }
          .nv-header-details { gap: 22px; padding-bottom: 16px; }
          .nv-buyer { margin: 18px 0; }
          .nv-table-wrap { overflow: visible; }
          .nv-items { font-size: 10px; }
          .nv-items th, .nv-items td { padding: 7px 5px; }
          .nv-items thead { display: table-header-group; }
          .nv-items tr { break-inside: avoid; }
          .nv-summary { gap: 22px; margin-top: 20px; break-inside: avoid; }
          .nv-invoice-footer { margin-top: 24px; padding-top: 18px; break-inside: avoid; }
          .nv-invoice::before { font-size: 46px; }
        }

        /* A4 printable-width correction */
        @media print {
          @page {
            size: A4 portrait;
            margin: 14mm 15mm;
          }

          .nv-invoice,
          .nv-invoice *,
          .nv-invoice *::before,
          .nv-invoice *::after {
            box-sizing: border-box;
          }

          .nv-invoice {
            width: 100% !important;
            max-width: 180mm !important;
          }

          .nv-header-details {
            grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
            gap: 6mm;
          }

          .nv-buyer {
            grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
          }

          .nv-summary {
            grid-template-columns: minmax(0, 1fr) minmax(0, 1.2fr);
            gap: 6mm;
          }

          .nv-header-details > *,
          .nv-buyer > *,
          .nv-summary > * {
            min-width: 0;
          }

          .nv-detail > span:first-child {
            flex: 0 1 44%;
            min-width: 0;
          }

          .nv-detail > span:last-child {
            flex: 1 1 56%;
            min-width: 0;
            overflow-wrap: anywhere;
          }

          .nv-table-wrap {
            width: 100%;
            max-width: 100%;
          }

          .nv-items {
            width: 100%;
            table-layout: fixed;
            font-size: 9px;
          }

          .nv-items th,
          .nv-items td {
            padding: 7px 3px;
            white-space: normal !important;
            overflow-wrap: anywhere;
            vertical-align: top;
          }

          .nv-items th:first-child,
          .nv-items td:first-child {
            width: 27%;
          }

          .nv-grand {
            font-size: 16px;
          }

          .nv-grand strong {
            white-space: nowrap;
          }
        }
      `}</style>
    </div>
  );
}
